import { TestImage } from "@assets/constant/index.constant";
import { CashIcon, InvoiceIcon } from "@assets/icons";
import { endpoint } from "@services/api.services";
import { GeneralCard, TableCard } from "@ui/internal/components/card";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { IndividualQuery, QueryData } from "@utils/interface/index.interface";
import {
  InvoiceBatchSummaryData,
  InvoiceData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { useMetaStore, useSelectionStore } from "@utils/states/index.state";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import { format } from "date-fns";
import { Wallet } from "lucide-react";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router";
import { ColumnList } from "./column/index.column";
import InvoiceForm from "./form/invoice.form";
import FilterPopover from "@ui/internal/components/filter-popover";
import { exportToExcel, showToast } from "@utils/helper/index.helper";

type Props = {};

const SummaryStatCard = ({
  icon,
  label,
  value,
  isLoading,
}: {
  icon: React.ReactNode;
  label: string;
  value?: number | null;
  isLoading?: boolean;
}) => (
  <div className="py-[23px] bg-white px-[32px] flex justify-start items-center gap-[24px] rounded-[8px] border border-[#ECECEC]">
    <span className="p-2 max-w-[250px] max-h-[250px] flex items-center justify-center rounded-full border border=2">
      {icon}
    </span>
    <div>
      <span className="text-[#969696] text-nowrap text-[14px]">{label}</span>
      <h5 className="font-bold text-[#2C2C2C] text-[36px] leading-[50px]">
        {isLoading ? (
          <Skeleton className="h-[50px] w-[90px]" />
        ) : (
          `NRs. ${(value ?? 0).toLocaleString()}`
        )}
      </h5>
    </div>
  </div>
);

const index: React.FC<Props> = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, date } = useMetaStore();
  const pathname = usePathname();
  const { handleAction } = useHandleAction({});

  /* ================================================= api calls logic ================================================= */
  const filterParams = {
    search,
    created_date__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
    created_date__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
    course_batch_id: searchParams.get("course-batch") || "",
    status: searchParams.get("status") || "",
    is_masterclass: pathname == "masterclass-invoice" ? true : false,
    is_jrp: pathname == "jrp-invoice" ? true : false,
    student:
      searchParams.get("student__in") || searchParams.get("student") || "",
    course_type: searchParams.get("course-type"),
  };

  const { data, isPending, refetch, error } = useListData<
    QueryData<InvoiceData>
  >({
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      ordering: searchParams.get("ordering") || "order",
      ...filterParams,
    },
    endpoint: endpoint["invoice"],
    key: `${pathname}-listing`,
    window: true,
  });

  const {
    data: batchSummaryData,
    isPending: isBatchSummaryPending,
  } = useListData<IndividualQuery<InvoiceBatchSummaryData>>({
    params: filterParams,
    endpoint: endpoint["invoice-batch-summary"],
    key: `${pathname}-batch-summary`,
    window: true,
  });
  // rollback
  useEffect(() => {
    if (error?.status === 404 && error?.data?.detail === "Invalid page.") {
      searchParams.set("tpage", "1");
      setSearchParams(searchParams);
    }
  }, [error]);

  const groupRefetch = () => {
    refetch();
  };

  const columns = ColumnList();
  /* ================================================= api calls logic ================================================= */
  const { selectedRowDetails } = useSelectionStore();
  const { mutate, isPending: sendInvoiceEmailPending } = usePostData();
  const handleInvoice = () => {
    mutate(
      {
        endpoint: endpoint["invoice-email"],
        data: { invoice_id: searchParams.get("mode-add-item") },
      },
      {
        onSuccess: ({ status }) => {
          if ([200].includes(status)) {
            showToast({
              title: "Success",
              description: "invoice email sent successfully",
              type: "success",
            });
            refetch();
            handleAction({ type: "mode-close" });
          }
        },
      }
    );
  };
  const summary = batchSummaryData?.data;
  return (
    <>
      <TableCard
        bulkActions
        table_content={{
          mode: "default",
          type: pathname,
        }}
        orientationPosition={false}
        tableHeaderData={
          summary ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 w-full justify-between items-center gap-4">
              <SummaryStatCard
                icon={<InvoiceIcon />}
                label="Total Amount"
                value={summary.total_amount}
                isLoading={isBatchSummaryPending}
              />
              {summary.total_paid_amount != null && (
                <SummaryStatCard
                  icon={<CashIcon />}
                  label="Paid Amount"
                  value={summary.total_paid_amount}
                  isLoading={isBatchSummaryPending}
                />
              )}
              {summary.total_due_amount != null && (
                <SummaryStatCard
                  icon={<Wallet size={32} />}
                  label="Due Amount"
                  value={summary.total_due_amount}
                  isLoading={isBatchSummaryPending}
                />
              )}
            </div>
          ) : undefined
        }
        inject_ui_table_actions={
          <>
            {data && data?.data?.results?.length > 0 && (
              <Button
                type="button"
                onClick={() =>
                  exportToExcel(
                    selectedRowDetails ?? data?.data?.results ?? [],
                    `${pathname}.xlsx`
                  )
                }
                variant={"outline"}
              >
                Export .xlsx
              </Button>
            )}
            <FilterPopover
              filters={[
                {
                  label: "Course Batch",
                  endpointValue: "course-batch",
                  queries: {
                    is_masterclass: "false",
                  },
                  format: {
                    key: "name",
                    value: "id",
                  },
                },
                {
                  label: "Student",
                  endpointValue: "student",
                  format: {
                    key: "email",
                    value: "id",
                  },
                },
                {
                  label: "Status",
                  searchKey: "status",
                  radio: true,
                  defaultOptions: [
                    {
                      label: "Sent",
                      value: "sent",
                    },

                    {
                      label: "Not Sent",
                      value: "not_sent",
                    },
                  ],
                },
                {
                  label: "Course Type",
                  endpointValue: "course-type",

                  format: {
                    key: "name",
                    value: "id",
                  },
                },
              ]}
              title="Filter"
              triggerText="Filter"
            />
          </>
        }
        endpoint={endpoint["invoice"]}
        grid={
          <GeneralCard
            showCheck
            content={{
              image: TestImage,
              title: "Focused Mode",
              tags: ["Clarity"],
            }}
            onDelete={() => {}}
            onEdit={() => {}}
          />
        }
        formId={pathname}
        columns={columns}
        refetch={groupRefetch}
        isPending={isPending}
        rows={data?.data?.results ?? []}
        sortableFilter={{
          search: search,
          ordering: searchParams.get("ordering") || "order",
          status: searchParams.get("status") || "",
          is_masterclass: pathname == "masterclass-invoice" ? true : false,
          is_jrp: pathname == "jrp-invoice" ? true : false,
        }}
        count={data?.data?.count ?? 10}
        form={<InvoiceForm />}
      />

      {searchParams.get("mode") && (
        <Dialog
          open={searchParams.get("mode") ? true : false}
          onOpenChange={() => {
            handleAction({ type: "mode-close" });
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Invoice</DialogTitle>
              <DialogDescription>
                You are about to send invoice, click send to continue.
                <div className="flex space-x-2 mt-[20px] justify-end">
                  <Button
                    onClick={() => handleAction({ type: "mode-close" })}
                    variant={"ghost"}
                  >
                    Cancle
                  </Button>
                  <Button
                    onClick={handleInvoice}
                    variant={"default"}
                    disabled={sendInvoiceEmailPending}
                    endIcon={sendInvoiceEmailPending ? <Spinner /> : null}
                  >
                    Send Invoice
                  </Button>
                </div>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default index;
